import React from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, 
  RefreshCw,
  FileText,
  CheckCircle,
  Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const XableSystemLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            Nothing changes without proof.
            <br />
            Every decision can be explained.
          </h1>
          <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Xable is a system where decisions are verified, tracked, and replayable.
          </p>
          <div className="flex items-center justify-center space-x-6">
            <Link to="/dashboard" className="bg-white text-[#0a0a0f] px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              View Demo
            </Link>
            <Link to="/verify" className="border border-gray-600 text-gray-300 px-8 py-3 rounded-lg font-medium hover:border-gray-400 hover:text-white transition-colors">
              Verify a Package
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Problem Section */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">The Problem</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: 'Decisions Cannot Be Reconstructed',
                description: 'Once a decision is made, the context fades. Why was this chosen? What alternatives existed? The reasoning disappears.'
              },
              {
                icon: RefreshCw,
                title: 'Reasoning Is Lost',
                description: 'Meeting notes, email threads, and Slack messages become fragmented. The human judgment behind decisions is scattered across systems.'
              },
              {
                icon: CheckCircle,
                title: 'Accountability Happens After Failure',
                description: 'When something goes wrong, organizations scramble to understand what happened. The original decision context is already gone.'
              }
            ].map((problem, index) => (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-8"
              >
                <problem.icon className="w-10 h-10 text-gray-500 mb-4" />
                <h3 className="text-xl font-semibold mb-3">{problem.title}</h3>
                <p className="text-gray-400 leading-relaxed">{problem.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: 'Verify',
                description: 'State changes are cryptographically verified and chained'
              },
              {
                step: 'Track Change',
                description: 'Every modification creates an immutable record'
              },
              {
                step: 'Chain',
                description: 'State evolution forms a verifiable timeline'
              },
              {
                step: 'Decide',
                description: 'Human choices are recorded with context and reasoning'
              },
              {
                step: 'Replay',
                description: 'Original decision context can be reconstructed exactly'
              },
              {
                step: 'Learn',
                description: 'Decision quality is evaluated against outcomes, not hindsight'
              },
              {
                step: 'Govern',
                description: 'Policy constraints are visible, not enforced'
              },
              {
                step: 'Share',
                description: 'Complete packages can be independently verified anywhere'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
                className="bg-gray-900/30 border border-gray-800 rounded-lg p-6"
              >
                <h3 className="text-lg font-semibold mb-2">{item.step}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Legal Moment */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-900/50 border border-gray-800 rounded-2xl p-12"
        >
          <div className="text-center mb-8">
            <div className="text-2xl text-gray-300 mb-4">"Did you use AI?"</div>
            <div className="text-xl text-white font-medium">"Here is exactly what was verified."</div>
          </div>
          <p className="text-gray-400 text-center leading-relaxed">
            In legal proceedings, audit reviews, and compliance checks, the question is not whether AI was used. The question is whether the decision process can be reconstructed and verified.
          </p>
        </motion.div>
      </section>

      {/* Cross-Domain */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">Cross-Domain</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { domain: 'Legal', description: 'Case decisions, evidence tracking, compliance' },
              { domain: 'Code', description: 'Repo changes, pull requests, deployment decisions' },
              { domain: 'Audit', description: 'Financial reviews, risk assessments, findings' },
              { domain: 'AI Workflows', description: 'Model decisions, prompt choices, output validation' }
            ].map((item, index) => (
              <motion.div
                key={item.domain}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="bg-gray-900/30 border border-gray-800 rounded-lg p-6 text-center"
              >
                <h3 className="text-xl font-semibold mb-2">{item.domain}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Unified Timeline Preview */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">Unified Timeline</h2>
          <div className="space-y-6">
            {[
              { step: '[Step 1] Legal Filing', detail: '+ Evidence introduced' },
              { step: '[Step 2] Repo Change', detail: '+ user.ts added' },
              { step: '[Step 3] Audit Finding', detail: '+ Risk identified' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 + index * 0.1 }}
                className="bg-gray-900/30 border border-gray-800 rounded-lg p-6"
              >
                <div className="font-semibold mb-1">{item.step}</div>
                <div className="text-gray-400 text-sm">{item.detail}</div>
              </motion.div>
            ))}
          </div>
          <p className="text-gray-500 text-sm mt-6 text-center">Identical structure across all domains</p>
        </motion.div>
      </section>

      {/* Decision Review Preview */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">Decision Review</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 space-y-6">
            <div>
              <div className="text-sm text-gray-500 mb-2">State Summary</div>
              <div className="text-white">3 unresolved items, 1 contradiction detected</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-2">Timeline</div>
              <div className="text-white">5 steps from initial filing to current state</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-2">Narrative</div>
              <div className="text-white">Evidence gap identified in claim section</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-2">Suggested Next Steps</div>
              <div className="text-white">Contradiction follow-up, evidence gap resolution</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-2">Governance Signals</div>
              <div className="text-white">Review required (contradiction present)</div>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-6 text-center">No action buttons. Review only.</p>
        </motion.div>
      </section>

      {/* Export / Proof */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">Verifiable Decision Package</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Lock className="w-5 h-5 text-green-500" />
                <span className="text-green-500 font-medium">Package: VALID</span>
              </div>
              <div className="flex items-center space-x-3">
                <Eye className="w-5 h-5 text-green-500" />
                <span className="text-green-500 font-medium">Chain: MATCH</span>
              </div>
            </div>
            <div className="bg-gray-950 rounded-lg p-4 font-mono text-sm text-gray-400 mb-4">
              sha256:a1b2c3d4e5f6...
            </div>
            <div className="text-gray-500 text-sm">
              This package can be independently verified anywhere. No server required.
            </div>
          </div>
        </motion.div>
      </section>

      {/* Closing */}
      <section className="max-w-4xl mx-auto px-6 py-32 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
            We didn't automate decisions.
            <br />
            We made them impossible to hide.
          </h2>
          <p className="text-xl text-gray-400">
            Xable — A system where decisions are verified, tracked, and replayable.
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-gray-500 text-sm">
          <p>Xable Decision Verification System</p>
          <p className="mt-2">© 2026. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
